import i18n from "../../../i18n";
import { Trans } from "react-i18next";
import { useState, useRef } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { Dialog, DialogOverlay, DialogPortal, DialogTitle } from "../../../components/ui/dialog";
import { cn } from "../../../lib/utils";
import { createPoll, updatePoll } from "../../../services/pollServices";
import { usePollForm, PollFormFields } from "./PollFormFields";

/**
 * Screen 1 — Create Poll.
 * Single-column form with a sticky action bar. Every creator publishes
 * directly and there is no scheduling — a published poll opens today — so the
 * primary action is always Publish.
 */
export function CreatePollModal({ open, initialData, onClose }) {
  const isEdit = Boolean(initialData?.pollId);

  const { form, set, errors, isValid, needsOptions, touched, setTouched, toPayload } =
    usePollForm(initialData);

  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const busyRef = useRef(false);

  const runSave = async (fn, setBusy, successMessage) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setSubmitError("");
    setBusy(true);
    try {
      await fn();
      onClose(true, successMessage);
    } catch (err) {
      setSubmitError(err.message || "Something went wrong. Please try again.");
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const handleSaveDraft = () => {
    if (!form.pollTitle.trim()) {
      setTouched(true);
      setSubmitError("Enter a poll title before saving as a draft.");
      return;
    }
    runSave(
      () => (isEdit ? updatePoll(initialData.pollId, toPayload(), "Draft") : createPoll(toPayload(), "Draft")),
      setSavingDraft,
      "Saved as draft."
    );
  };

  // Publish goes straight through — no confirmation step.
  const handlePrimary = () => {
    setTouched(true);
    if (!isValid) {
      setSubmitError("Please fill in all required fields marked with *");
      return;
    }
    runSave(
      () => (isEdit ? updatePoll(initialData.pollId, toPayload(), "Published") : createPoll(toPayload(), "Published")),
      setSubmitting,
      "Poll published successfully!"
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose(false)}>
      <DialogPortal>
        <DialogOverlay className="bg-[#0a1230]/50 backdrop-blur-xs" />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2",
            "w-[95vw] sm:w-[600px] max-w-[600px] max-h-[92vh] overflow-hidden flex flex-col",
            "bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)] border border-white/60 font-sans",
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          )}>

          <div className="flex items-center justify-between px-6 py-4 border-b border-(--color-text-border)/70 shrink-0">
            <DialogTitle className="text-[18px] font-bold text-foreground">
              {isEdit ? "Complete Draft" : "Create Poll"}
            </DialogTitle>
            <button
              type="button"
              onClick={() => onClose(false)}
              className="h-8 w-8 rounded-lg grid place-items-center text-muted-foreground hover:bg-workspace"
              aria-label={i18n.t("formActions.close")}>
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-5">
            <PollFormFields
              form={form}
              set={set}
              errors={errors}
              needsOptions={needsOptions}
              touched={touched} />
          </div>

          {submitError && (
            <div className="flex items-center gap-2 px-6 py-3 bg-error/10 border-t border-error/25 text-[13px] font-semibold text-error">
              <span aria-hidden="true">⚠</span> {submitError}
            </div>
          )}

          <div className="flex justify-end gap-2.5 px-6 py-4 border-t border-(--color-text-border)/70 shrink-0">
            <button
              type="button"
              onClick={() => onClose(false)}
              className="h-10 px-5 rounded-xl border border-(--color-text-border) bg-white text-sm font-semibold text-foreground hover:bg-workspace">
              <Trans i18nKey="formActions.cancel" />
            </button>
            <button
              type="button"
              onClick={handleSaveDraft}
              disabled={submitting || savingDraft}
              className="h-10 px-5 rounded-xl border border-(--color-text-border) bg-white text-sm font-semibold text-accent hover:bg-workspace disabled:opacity-50">
              {savingDraft ? "Saving…" : "Save as Draft"}
            </button>
            <button
              type="button"
              onClick={handlePrimary}
              disabled={submitting || savingDraft}
              className="h-10 px-5 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed">
              {submitting ? "Working…" : "Publish"}
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
